/**
 * Comprensión híbrida (pura): primero las reglas locales; solo si no están seguras se consulta
 * una IA a través de `request`, y su respuesta pasa por el mismo esquema (`validateUnderstanding`).
 * Si la IA no responde, tarda demasiado, devuelve algo inválido o no mejora a las reglas, se usa
 * lo de las reglas. La IA nunca ve registros: solo el mensaje, la fecha, los nombres y si hay
 * algo pendiente. La usan la app (vía su servidor) y el canal de WhatsApp (directamente).
 */
import { isoWeekday, type LocalDate } from '@habitos/engine';
import { validateUnderstanding, type Changes, type Intent, type Understander, type Understanding, type UnderstandContext } from './intents';
import { simplify } from './normalize';
import { understandDeterministic } from './understand';

/** Las reglas resuelven solas las órdenes de control y lo que entienden con seguridad. */
const RULES_ONLY = new Set(['confirm', 'cancel', 'reset', 'greeting', 'thanks', 'help', 'undo_last', 'alternative', 'command']);
/** Lo que la IA nunca decide: aceptar, descartar o reiniciar. Deshacer sí (siempre pide confirmación). */
const NOT_FROM_AI = new Set(['confirm', 'cancel', 'reset', 'command']);
export const RULES_CONFIDENT = 0.8;

/**
 * Los modelos pequeños suelen rellenar con null los campos que no se mencionan. En "changes",
 * null y ausente significan lo mismo para la IA: se quitan antes de validar (un "sin hora"
 * explícito lo siguen entendiendo las reglas).
 */
export function cleanAiOutput(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const strip = (c: unknown) => (c && typeof c === 'object' ? Object.fromEntries(Object.entries(c).filter(([, v]) => v !== null)) : c);
  const r = raw as { intent?: Record<string, unknown> };
  const intent = r.intent && typeof r.intent === 'object' ? { ...r.intent } : r.intent;
  if (intent && typeof intent === 'object') {
    for (const [k, v] of Object.entries(intent)) if (v === null && k !== 'activity' && k !== 'windows' && k !== 'habitText' && k !== 'taskText' && k !== 'date' && k !== 'minutes') delete intent[k];
    if ('changes' in intent) intent.changes = strip(intent.changes);
    if (Array.isArray(intent.activities)) intent.activities = intent.activities.map((a: unknown) => (a && typeof a === 'object' ? { ...a, changes: strip((a as { changes?: unknown }).changes ?? {}) } : a));
  }
  return { ...r, intent };
}

const TIME_CUE = /\d|\b(?:manana|tarde|noche|mediodia|madrugada|temprano|hora|horas|am|pm)\b/;
const DAY_CUE = /\b(?:lunes|martes|miercoles|jueves|viernes|sabados?|domingos?|finde|fines? de semana|diari|cada|todos|todas|semana|semanal|veces|vez|dias?|laborables)\b/;
const AMOUNT_CUE = /\d|\b(?:minutos?|horas?|hora|media|cuarto|un rato)\b/;

/**
 * Lo que el mensaje no dice, la IA no lo puede añadir: sin pistas de hora, días o duración en
 * el texto, esos campos se descartan y la conversación los pregunta.
 */
export function dropInvented(u: Understanding, text: string): Understanding {
  const n = simplify(text);
  const fix = (c: Changes): Changes => {
    const out = { ...c };
    const drop = (keys: (keyof Changes)[]) => keys.forEach((k) => delete out[k]);
    if (!TIME_CUE.test(n)) drop(['time', 'timeOptions', 'slot']);
    if (!DAY_CUE.test(n)) drop(['days', 'except', 'quota', 'freeDays']);
    if (!AMOUNT_CUE.test(n)) drop(['minutes', 'weeklyMinutes']);
    return out;
  };
  const i = u.intent;
  if ('changes' in i) return { ...u, intent: { ...i, changes: fix(i.changes) } as Intent };
  if (i.type === 'plan_routine') return { ...u, intent: { ...i, activities: i.activities.map((a) => ({ ...a, changes: fix(a.changes) })) } };
  return u;
}

export function hybridUnderstander(opts: { timeoutMs?: number; request: (body: unknown, signal: AbortSignal) => Promise<unknown> }): Understander {
  const request = opts.request;
  return {
    id: 'hybrid',
    async understand(text: string, ctx: UnderstandContext): Promise<Understanding> {
      const rules = understandDeterministic(text, ctx);
      if (RULES_ONLY.has(rules.intent.type) || (rules.intent.type !== 'unknown' && rules.confidence >= RULES_CONFIDENT)) return rules;
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 20_000);
      try {
        const raw = await request(
          {
            text,
            today: ctx.today,
            weekday: isoWeekday(ctx.today as LocalDate),
            habits: ctx.habits.filter((h) => !h.archived && !h.completed).slice(0, 80).map((h) => ({ id: h.id, name: h.name, kind: h.kind })),
            pending: ctx.pending,
          },
          ctrl.signal,
        );
        const ai = validateUnderstanding(cleanAiOutput(raw));
        // La IA no puede "confirmar" ni "cancelar" por su cuenta: eso lo deciden las reglas.
        if (ai.intent.type === 'unknown' || NOT_FROM_AI.has(ai.intent.type) || ai.confidence <= rules.confidence) return rules;
        if ((ai.intent.type === 'modify' || ai.intent.type === 'remove_item') && !ctx.pending) return rules;
        return { ...dropInvented(ai, text), via: 'ai' };
      } catch {
        return rules;
      } finally {
        clearTimeout(timer);
      }
    },
  };
}
